import {
  Text,
  IconPeopleIdCard,
  IconUser,
  IconColumns,
  MagicTableFilterConfig,
  useMagicTableInfiniteQuery,
  useLinkMagicTableWithSearchParams,
  useMagicTableSelectAll,
  ActiveFilter
} from '@sequencehq/core-components'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import { QuotesFilters } from 'modules/Quotes/types'
import { useCallback, useEffect, useState } from 'react'
import { useForcedActiveFilters } from 'SavedViews/hooks/useForcedActiveFilters'
import { customerIdFilterConfig } from 'Customer/index'

type UseQuotesMagicTable = ({
  forcedActiveFilters,
  onFiltersApiQueryParamsChanged
}: {
  forcedActiveFilters?: ActiveFilter[]
  onFiltersApiQueryParamsChanged?: (newParams: string) => void
}) => {
  filters: MagicTableFilterConfig<QuotesFilters>[]
  activeFilters: ActiveFilter[]
  sortBy: string | null
  onChangeActiveFilters: (newActiveFilters: ActiveFilter[]) => void
  onChangeSortBy: (sortBy: string | null) => void
  infiniteQuery: {
    data?: any
    isFetching: boolean
    hasNextPage: boolean
    isFetchingNextPage: boolean
    fetchNextPage: () => void
    isLoading: boolean
  }
  selectedQuotes: string[]
  setSelectedQuotes: (selectedQuotes: string[]) => void
  selectAll: {
    showPrompt: boolean
    showClearSelection: boolean
    onClearSelection: () => void
    onSelectAll: () => void
    loading: boolean
    total: number
    totalSelected: number
  }
}

export const useQuotesMagicTable: UseQuotesMagicTable = ({
  forcedActiveFilters = [],
  onFiltersApiQueryParamsChanged
}) => {
  // Function to fetch users for the owner filter
  const fetchUserOptions = useCallback(async () => {
    try {
      // Fetch users from the organization
      const response = await dashboard20240730Client.getUsers({
        limit: 100
      })
      const users = response.data?.items || []

      // Map users to options format
      return users.map(user => ({
        value: user.id,
        label: user.email || 'Unknown User'
      }))
    } catch (error) {
      console.error('Error fetching users:', error)
      return []
    }
  }, [])

  const filtersConfig: MagicTableFilterConfig<QuotesFilters>[] = [
    {
      type: 'text',
      paramName: 'title',
      label: 'Title',
      icon: IconPeopleIdCard
    },
    {
      ...customerIdFilterConfig,
      paramName: 'customerId'
    },
    {
      type: 'multiSelect',
      paramName: 'createdBy',
      label: 'Owner',
      icon: IconUser,
      options: [],
      optionsFunc: fetchUserOptions,
      format: (value: string) => <Text>{value}</Text>
    },
    {
      type: 'multiSelect',
      paramName: 'status',
      label: 'Status',
      icon: IconColumns,
      options: [
        { value: 'DRAFT', label: 'Draft' },
        { value: 'PUBLISHED', label: 'Published' },
        { value: 'ACCEPTED', label: 'Signed' },
        { value: 'EXECUTED', label: 'Executed' }
      ],
      format: (value: string) => <Text>{value}</Text>
    },
    {
      paramName: 'includeArchived',
      type: 'toggle',
      label: 'Show archived quotes'
    }
  ]

  // Apply disabled state to filters that match forcedActiveFilters
  const filters = filtersConfig.map(filter => ({
    ...filter,
    disabled: !!forcedActiveFilters.find(
      forcedFilter => forcedFilter.paramName === filter.paramName
    )
  }))

  const {
    activeFilters: searchActiveFilters,
    onChangeActiveFilters: onChangeSearchActiveFilters,
    sortBy,
    onChangeSortBy
  } = useLinkMagicTableWithSearchParams(filters)

  const { activeFilters, onChangeActiveFilters } = useForcedActiveFilters({
    forcedActiveFilters,
    searchActiveFilters,
    onChangeSearchActiveFilters
  })

  // Expand "PUBLISHED" status to include READY_TO_SIGN and PREPARING_SIGNATURE_REQUEST for API calls
  // This keeps the URL and filter display clean while sending all relevant statuses to the backend
  const expandedStatusParams: Record<string, string> | undefined = (() => {
    const statusFilter = activeFilters.find(
      filter => filter.paramName === 'status' && filter.type === 'multiSelect'
    )
    if (!statusFilter || statusFilter.type !== 'multiSelect') {
      return undefined
    }

    const expandedStatuses = statusFilter.values.flatMap(status => {
      if (status === 'PUBLISHED') {
        return ['PUBLISHED', 'READY_TO_SIGN', 'PREPARING_SIGNATURE_REQUEST']
      }
      return [status]
    })

    const uniqueStatuses = Array.from(new Set(expandedStatuses))
    return { status: uniqueStatuses.join(',') }
  })()

  const quotesPerPage = 100
  const { infiniteQuery } = useMagicTableInfiniteQuery(
    dashboard20240730Client.getQuotes,
    filters,
    activeFilters,
    sortBy,
    quotesPerPage,
    undefined, // maxPages
    expandedStatusParams // Override status param for API call
  )

  const [prevQueryParams, setPrevQueryParams] = useState<string>('')

  useEffect(() => {
    if (onFiltersApiQueryParamsChanged) {
      const queryParams = new URLSearchParams()

      activeFilters.forEach(filter => {
        if (filter.type === 'multiSelect' && Array.isArray(filter.values)) {
          filter.values.forEach(value => {
            queryParams.append(filter.paramName, value)
          })
        } else if (filter.type === 'toggle' && filter.value) {
          queryParams.set(filter.paramName, 'true')
        } else if (filter.type === 'text' && filter.value) {
          queryParams.set(filter.paramName, filter.value)
        }
      })

      const nextQueryParams = queryParams.toString()

      // Only update if the params have changed to prevent unnecessary rerenders
      if (nextQueryParams !== prevQueryParams) {
        setPrevQueryParams(nextQueryParams)
        onFiltersApiQueryParamsChanged(nextQueryParams)
      }
    }
  }, [activeFilters, onFiltersApiQueryParamsChanged, prevQueryParams])

  const { selectedIds, setSelectedIds, selectAll } = useMagicTableSelectAll({
    infiniteQuery,
    activeFilters,
    pageSize: quotesPerPage
  })

  return {
    filters,
    activeFilters,
    sortBy,
    onChangeActiveFilters,
    onChangeSortBy,
    infiniteQuery,
    selectedQuotes: selectedIds,
    setSelectedQuotes: setSelectedIds,
    selectAll
  }
}
